import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Register the built-in data type view providers

import { AbBooleanRenderer } from "abreact/data/AbBooleanRenderer";
import { AbDataValueAccessorTooltipContent } from "abreact/data/AbDataValueTooltipContent";
import { BuiltInDataTypes } from "abreact/data/DataType";
import { isScalarAccessor } from "abreact/data/DataValueAccessor";
import { GuidedExpressionRenderer } from "abreact/expression/ui/readonly/GuidedExpressionRenderer";
import { AbBooleanField } from "abreact/form/AbBooleanField";
import { AbColorField } from "abreact/form/AbColorField";
import { AbDateField } from "abreact/form/AbDateField";
import { AbDateTimeField } from "abreact/form/AbDateTimeField";
import { AbDecimalField } from "abreact/form/AbDecimalField";
import { AbGuidedExpressionField } from "abreact/form/AbGuidedExpressionField";
import { AbHtmlField } from "abreact/form/AbHtmlField";
import { AbIconField } from "abreact/form/AbIconField";
import { AbIntegerField } from "abreact/form/AbIntegerField";
import { AbMarkdownField } from "abreact/form/AbMarkdownField";
import { AbStringField } from "abreact/form/AbStringField";
import { AbUnsetValue } from "abreact/form/AbUnsetValue";
import { GenericGridMarkdownEditor } from "abreact/grid/generic/GenericGridMarkdownEditor";
import { MaxLineGridCellRenderer } from "abreact/grid/generic/MaxLineGridCellRenderer";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { AbBooleanDataValueInput } from "abreact/input/AbBooleanDataValueInput";
import { AbColorDataValueInput } from "abreact/input/AbColorDataValueInput";
import { AbDateDataValueInput } from "abreact/input/AbDateDataValueInput";
import { AbDateTimeDataValueInput } from "abreact/input/AbDateTimeDataValueInput";
import { AbDecimalDataValueInput } from "abreact/input/AbDecimalDataValueInput";
import { AbHtmlDataValueInput } from "abreact/input/AbHtmlDataValueInput";
import { AbIconDataValueInput } from "abreact/input/AbIconDataValueInput";
import { AbIntegerDataValueInput } from "abreact/input/AbIntegerDataValueInput";
import { AbMarkdownDataValueInput } from "abreact/input/AbMarkdownDataValueInput";
import { AbMultiLinePlainStringDataValueInput } from "abreact/input/AbMultiLinePlainStringDataValueInput";
import { AbPasswordDataValueInput } from "abreact/input/AbPasswordDataValueInput";
import { AbSecretDataValueInput } from "abreact/input/AbSecretDataValueInput";
import { AbSingleLinePlainStringDataValueInput } from "abreact/input/AbSingleLinePlainStringDataValueInput";
import { startsWithRichContent } from "abreact/input/markdown/utils/MarkdownUtils";
import { AbColorViewer } from "abreact/misc/AbColorViewer";
import { AbHtmlContent } from "abreact/misc/AbHtmlContent";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbItem } from "abreact/misc/AbItem";
import { AbMarkdownContent } from "abreact/misc/AbMarkdownContent";
import { AbSize } from "abreact/misc/AbSize";
import { AbTextContent } from "abreact/misc/AbTextContent";
import { AbWebServices } from "abweb/service/AbWebServices";
import { AbDecimal } from "abweb/utils/AbDecimal";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { AbCalendarDay } from "abweb/utils/date/AbCalendarDay";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { ExtensionPoints } from "abweb/utils/plugins/ExtensionPoints";
import { StringUtils } from "abweb/utils/StringUtils";
import * as React from "react";
export const PASSWORD_INDICATOR = "********";
export const SECRET_INDICATOR = "********************************";
export class DataValueViews {
  static getViewProvider(dataType) {
    let viewProvider = this.viewProviders.get(dataType);
    if (!viewProvider && dataType.rawType) {
      viewProvider = this.viewProviders.get(dataType.rawType);
    }
    if (!viewProvider) {
      throw new Error(`Unable to find view provider for data type ${dataType.name}`);
    }
    return viewProvider;
  }
  static registerBuiltInDataValueViews() {
    DataValueViews.viewProviders.registry.register(() => new StringDataValueViewProvider()).register(() => new MultiValueStringDataValueViewProvider()).register(() => new SingleLineStringDataValueViewProvider()).register(() => new PasswordDataValueViewProvider()).register(() => new SecretDataValueViewProvider()).register(() => new BooleanDataValueViewProvider()).register(() => new IntegerDataValueViewProvider()).register(() => new DecimalDataValueViewProvider()).register(() => new DateDataValueViewProvider()).register(() => new DateTimeDataValueViewProvider()).register(() => new HtmlDataValueViewProvider()).register(() => new MarkdownDataValueViewProvider()).register(() => new GuidedExpressionDataValueViewProvider()).register(() => new IconDataValueViewProvider()).register(() => new ColorDataValueViewProvider());
  }
}
_defineProperty(DataValueViews, "viewProviders", ExtensionPoints.syncKeyedValues(value => value.dataType));
class StringDataValueViewProvider {
  constructor(dataType = BuiltInDataTypes.STRING, singleLine = false) {
    _defineProperty(this, "dataType", void 0);
    _defineProperty(this, "singleLine", void 0);
    _defineProperty(this, "renderInput", props => {
      if (isScalarAccessor(props.accessor)) {
        const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
        const constraints = props.accessor.constraints;
        return this.singleLine || this.dataType === BuiltInDataTypes.SINGLE_LINE_PLAIN_STRING || constraints?.singleLine || "singleLine" in props && props.singleLine ? /*#__PURE__*/React.createElement(AbSingleLinePlainStringDataValueInput, _extends({}, props, {
          accessor: props.accessor,
          currentValue: currentValue
        })) : /*#__PURE__*/React.createElement(AbMultiLinePlainStringDataValueInput, _extends({}, props, {
          accessor: props.accessor,
          currentValue: currentValue
        }));
      }
      throw new Error(`Unable to render string input for data value "${props.accessor.name}"`);
    });
    _defineProperty(this, "renderField", props => {
      const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbStringField, _extends({}, props, {
        subject: props.subject,
        accessor: props.accessor,
        currentValue: currentValue,
        singleLine: this.singleLine
      }));
    });
    this.dataType = dataType;
    this.singleLine = singleLine;
  }
  isApplicableType(value) {
    return typeof value === "string";
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      return typeof value === "string" ? /*#__PURE__*/React.createElement(AbTextContent, {
        text: value,
        singleLine: this.singleLine,
        truncateLongContent: true
      }) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
    }
    return null;
  }
}
class MultiValueStringDataValueViewProvider extends StringDataValueViewProvider {
  constructor() {
    super(BuiltInDataTypes.MULTI_LINE_PLAIN_STRING);
  }
}
export class SingleLineStringDataValueViewProvider extends StringDataValueViewProvider {
  constructor(dataType = BuiltInDataTypes.SINGLE_LINE_PLAIN_STRING) {
    super(dataType, true);
  }
}
class PasswordDataValueViewProvider extends StringDataValueViewProvider {
  constructor() {
    super(BuiltInDataTypes.PASSWORD, true);
    _defineProperty(this, "renderValue", props => {
      if (isScalarAccessor(props.accessor)) {
        const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
        return typeof value === "string" ? PASSWORD_INDICATOR : /*#__PURE__*/React.createElement(AbUnsetValue, null);
      }
      return null;
    });
    _defineProperty(this, "renderInput", props => {
      if (isScalarAccessor(props.accessor)) {
        const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
        return /*#__PURE__*/React.createElement(AbPasswordDataValueInput, _extends({}, props, {
          accessor: props.accessor,
          currentValue: currentValue
        }));
      }
      throw new Error(`Unable to render password input for data value "${props.accessor.name}"`);
    });
  }
}
class SecretDataValueViewProvider extends StringDataValueViewProvider {
  constructor() {
    super(BuiltInDataTypes.SECRET, false);
    _defineProperty(this, "renderValue", props => {
      if (isScalarAccessor(props.accessor)) {
        const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
        return typeof value === "string" ? SECRET_INDICATOR : /*#__PURE__*/React.createElement(AbUnsetValue, null);
      }
      return null;
    });
    _defineProperty(this, "renderInput", props => {
      if (isScalarAccessor(props.accessor)) {
        const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
        return /*#__PURE__*/React.createElement(AbSecretDataValueInput, _extends({}, props, {
          subject: props.subject,
          accessor: props.accessor,
          currentValue: currentValue
        }));
      }
      throw new Error(`Unable to render secret input for data value "${props.accessor.name}"`);
    });
  }
}
export class BooleanDataValueViewProvider {
  constructor() {
    _defineProperty(this, "dataType", BuiltInDataTypes.BOOLEAN);
  }
  isApplicableType(value) {
    return typeof value === "boolean";
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      return typeof value === "boolean" ? /*#__PURE__*/React.createElement(AbBooleanRenderer, {
        value: value
      }) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
    }
    return null;
  }
  renderInput(props) {
    if (isScalarAccessor(props.accessor)) {
      const currentValue = typeof props.currentValue === "boolean" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbBooleanDataValueInput, _extends({}, props, {
        accessor: props.accessor,
        currentValue: currentValue
      }));
    }
    throw new Error(`Unable to render boolean input for data value "${props.accessor.name}"`);
  }
  renderField(props) {
    const currentValue = typeof props.currentValue === "boolean" || props.currentValue === null ? props.currentValue : undefined;
    return /*#__PURE__*/React.createElement(AbBooleanField, _extends({}, props, {
      subject: props.subject,
      accessor: props.accessor,
      currentValue: currentValue
    }));
  }
}
export class IntegerDataValueViewProvider {
  constructor() {
    _defineProperty(this, "dataType", BuiltInDataTypes.INTEGER);
  }
  isApplicableType(value) {
    return typeof value === "number";
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      return typeof value === "number" ? /*#__PURE__*/React.createElement("span", {
        className: "ab-font-feature-tabular-number-text"
      }, value.toLocaleString()) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
    }
    return null;
  }
  renderInput(props) {
    if (isScalarAccessor(props.accessor)) {
      const currentValue = typeof props.currentValue === "number" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbIntegerDataValueInput, _extends({}, props, {
        accessor: props.accessor,
        currentValue: currentValue
      }));
    }
    throw new Error(`Unable to render integer input for data value "${props.accessor.name}"`);
  }
  renderField(props) {
    const currentValue = typeof props.currentValue === "number" || props.currentValue === null ? props.currentValue : undefined;
    return /*#__PURE__*/React.createElement(AbIntegerField, _extends({}, props, {
      subject: props.subject,
      accessor: props.accessor,
      currentValue: currentValue
    }));
  }
}
class DecimalDataValueViewProvider {
  constructor() {
    _defineProperty(this, "dataType", BuiltInDataTypes.DECIMAL);
  }
  isApplicableType(value) {
    return ClassUtils.isInstanceOf(value, AbDecimal);
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      return ClassUtils.isInstanceOf(value, AbDecimal) ? /*#__PURE__*/React.createElement("span", {
        className: "ab-font-feature-tabular-number-text"
      }, value.getFormattedValue()) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
    }
    return null;
  }
  renderInput(props) {
    if (isScalarAccessor(props.accessor)) {
      const currentValue = ClassUtils.isInstanceOf(props.currentValue, AbDecimal) || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbDecimalDataValueInput, _extends({}, props, {
        accessor: props.accessor,
        currentValue: currentValue
      }));
    }
    throw new Error(`Unable to render decimal input for data value "${props.accessor.name}"`);
  }
  renderField(props) {
    const currentValue = ClassUtils.isInstanceOf(props.currentValue, AbDecimal) || props.currentValue === null ? props.currentValue : undefined;
    return /*#__PURE__*/React.createElement(AbDecimalField, _extends({}, props, {
      subject: props.subject,
      accessor: props.accessor,
      currentValue: currentValue
    }));
  }
}
class DateDataValueViewProvider {
  constructor() {
    _defineProperty(this, "dataType", BuiltInDataTypes.DATE);
  }
  isApplicableType(value) {
    return ClassUtils.isInstanceOf(value, AbCalendarDay);
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      return ClassUtils.isInstanceOf(value, AbCalendarDay) ? /*#__PURE__*/React.createElement("span", {
        className: "ab-font-feature-tabular-number-text"
      }, DateTimeUtils.DATE_SHORT.format(value.localMidnightDate)) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
    }
    return null;
  }
  renderInput(props) {
    if (isScalarAccessor(props.accessor)) {
      const currentValue = ClassUtils.isInstanceOf(props.currentValue, AbCalendarDay) || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbDateDataValueInput, _extends({}, props, {
        accessor: props.accessor,
        currentValue: currentValue
      }));
    }
    throw new Error(`Unable to render date input for data value "${props.accessor.name}"`);
  }
  renderField(props) {
    const currentValue = ClassUtils.isInstanceOf(props.currentValue, AbCalendarDay) || props.currentValue === null ? props.currentValue : undefined;
    return /*#__PURE__*/React.createElement(AbDateField, _extends({}, props, {
      subject: props.subject,
      accessor: props.accessor,
      currentValue: currentValue
    }));
  }
}
class DateTimeDataValueViewProvider {
  constructor() {
    _defineProperty(this, "dataType", BuiltInDataTypes.DATE_TIME);
  }
  isApplicableType(value) {
    return ClassUtils.isInstanceOf(value, Date);
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      return ClassUtils.isInstanceOf(value, Date) ? /*#__PURE__*/React.createElement("span", {
        className: "ab-font-feature-tabular-number-text"
      }, DateTimeUtils.DATETIME_WITH_MINUTES.format(value)) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
    }
    return null;
  }
  renderInput(props) {
    if (isScalarAccessor(props.accessor)) {
      const currentValue = ClassUtils.isInstanceOf(props.currentValue, Date) || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbDateTimeDataValueInput, _extends({}, props, {
        accessor: props.accessor,
        currentValue: currentValue
      }));
    }
    throw new Error(`Unable to render date input for data value "${props.accessor.name}"`);
  }
  renderField(props) {
    const currentValue = ClassUtils.isInstanceOf(props.currentValue, Date) || props.currentValue === null ? props.currentValue : undefined;
    return /*#__PURE__*/React.createElement(AbDateTimeField, _extends({}, props, {
      subject: props.subject,
      accessor: props.accessor,
      currentValue: currentValue
    }));
  }
}
class HtmlDataValueViewProvider extends StringDataValueViewProvider {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "dataType", BuiltInDataTypes.HTML_STRING);
    _defineProperty(this, "renderValue", props => {
      if (isScalarAccessor(props.accessor)) {
        const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
        return typeof value === "string" ? /*#__PURE__*/React.createElement(AbHtmlContent, {
          html: value,
          truncateLongContent: true
        }) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
      }
      return null;
    });
    _defineProperty(this, "renderInput", props => {
      if (isScalarAccessor(props.accessor)) {
        const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
        return /*#__PURE__*/React.createElement(AbHtmlDataValueInput, _extends({}, props, {
          accessor: props.accessor,
          currentValue: currentValue
        }));
      }
      throw new Error(`Unable to render HTML string input for data value "${props.accessor.name}"`);
    });
    _defineProperty(this, "renderField", props => {
      const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbHtmlField, _extends({}, props, {
        subject: props.subject,
        accessor: props.accessor,
        currentValue: currentValue
      }));
    });
  }
}
export class GuidedExpressionDataValueViewProvider extends StringDataValueViewProvider {
  constructor() {
    super(BuiltInDataTypes.GUIDED_EXPRESSION_JSON);
    _defineProperty(this, "renderValue", props => {
      if (isScalarAccessor(props.accessor)) {
        const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
        const expressionString = typeof value === "string" ? value : null;
        return /*#__PURE__*/React.createElement(GuidedExpressionRenderer, {
          expression: expressionString
        });
      }
      return null;
    });
    _defineProperty(this, "renderField", props => {
      const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbGuidedExpressionField, _extends({}, props, {
        subject: props.subject,
        accessor: props.accessor,
        currentValue: currentValue
      }));
    });
  }
}
class MarkdownDataValueViewProvider extends StringDataValueViewProvider {
  constructor() {
    super(BuiltInDataTypes.MARKDOWN_STRING);
    _defineProperty(this, "renderValue", props => {
      if (isScalarAccessor(props.accessor)) {
        const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
        return typeof value === "string" ? /*#__PURE__*/React.createElement(AbMarkdownContent, {
          value: value,
          truncateLongContent: true
        }) : /*#__PURE__*/React.createElement(AbUnsetValue, null);
      }
      return null;
    });
    _defineProperty(this, "renderInput", props => {
      if (isScalarAccessor(props.accessor)) {
        const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
        return /*#__PURE__*/React.createElement(AbMarkdownDataValueInput, _extends({}, props, {
          accessor: props.accessor,
          currentValue: currentValue
        }));
      }
      throw new Error(`Unable to render Markdown string input for data value "${props.accessor.name}"`);
    });
    _defineProperty(this, "renderField", props => {
      const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbMarkdownField, _extends({}, props, {
        subject: props.subject,
        accessor: props.accessor,
        currentValue: currentValue
      }));
    });
    _defineProperty(this, "renderGridCell", props => {
      const currentValue = props.currentValue ?? "";
      const isHighlighted = props.gridMachine?.genericGridFindRegExp != null && StringUtils.contains(props.gridCellProps.classNames, "dg-find-highlighted");
      const findRegExp = isHighlighted ? props.gridMachine?.genericGridFindRegExp : null;
      if (props.gridMachine?.isFixedRowHeightMode) {
        const rowId = props.gridMachine.rowIndexToIdMapper(props.gridCellProps.rowIndex) ?? null;
        const isRowExpanded = props.gridMachine.isRowExpanded(rowId);
        const rowItem = {
          rowId,
          colId: props.column.id
        };
        props.column.isHtmlContent = false;
        props.column.richDataTypeName = this.dataType.name;
        const isLeadingContentRich = startsWithRichContent(currentValue);
        if (isLeadingContentRich) {
          // If the first thing in the Markdown is rich (image, un/ordered list, table, etc.)
          // then we always need to show the Show More button instead of just on hover
          props.gridCellProps.classNames = CssUtils.composeClassNames(props.gridCellProps.classNames, "force-show-overlay-button");
        }

        // Note that we use  AbDataValueAccessorTooltipContent as a function directly, otherwise we get an empty ReactElement back but we really want null
        const tooltip = AbDataValueAccessorTooltipContent({
          accessor: props.accessor,
          subject: props.subject,
          valueRenderer: markdownValue => /*#__PURE__*/React.createElement(AbMarkdownContent, {
            markdown: markdownValue
          })
        });
        return /*#__PURE__*/React.createElement(MaxLineGridCellRenderer, {
          text: currentValue,
          isRowExpanded: isRowExpanded,
          isCellExpanded: props.gridMachine.isCellExpanded(rowItem),
          findRegExp: findRegExp,
          tooltip: tooltip,
          column: props.column,
          onExpandCell: expand => expand ? props.gridMachine?.addToExpandedRows(rowItem) : props.gridMachine?.removeFromExpandedRows(rowId)
        });
      }
      return /*#__PURE__*/React.createElement(AbMarkdownContent, {
        markdown: currentValue,
        isHighlighted: isHighlighted
      });
    });
    _defineProperty(this, "renderGridCellEditor", props => {
      return /*#__PURE__*/React.createElement(GenericGridMarkdownEditor, {
        initialValue: props.currentValue ?? "",
        onChange: newValue => props.onChange(props.subject, newValue)
      });
    });
  }
}
class IconDataValueViewProvider extends StringDataValueViewProvider {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "dataType", BuiltInDataTypes.ICON);
    _defineProperty(this, "renderField", props => {
      const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbIconField, _extends({}, props, {
        subject: props.subject,
        accessor: props.accessor,
        currentValue: currentValue
      }));
    });
    _defineProperty(this, "renderInput", props => {
      if (isScalarAccessor(props.accessor)) {
        const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
        return /*#__PURE__*/React.createElement(AbIconDataValueInput, _extends({}, props, {
          accessor: props.accessor,
          currentValue: currentValue
        }));
      }
      throw new Error(`Unable to render icon input for data value "${props.accessor.name}"`);
    });
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      if (typeof value !== "string") {
        return /*#__PURE__*/React.createElement(AbUnsetValue, null);
      }
      const iconIdentifierInfo = AbWebServices.getImageIdentifierManager().getIdentifierInfo(value);
      const iconSDKInfo = iconIdentifierInfo.extra;
      const icon = /*#__PURE__*/React.createElement(AbIcon, {
        image: value,
        tooltip: value,
        size: AbSize.LARGE
      });
      // Render the icon next to its display name.
      // If its display name is not available, just render the icon.
      return iconSDKInfo != null ? /*#__PURE__*/React.createElement(AbItem, {
        label: iconSDKInfo.semanticName,
        icon: icon
      }) : icon;
    }
    return null;
  }

  // TODO Implement Grid Methods
}
class ColorDataValueViewProvider extends StringDataValueViewProvider {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "dataType", BuiltInDataTypes.COLOR);
    _defineProperty(this, "renderField", props => {
      const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
      return /*#__PURE__*/React.createElement(AbColorField, _extends({}, props, {
        subject: props.subject,
        accessor: props.accessor,
        currentValue: currentValue
      }));
    });
    _defineProperty(this, "renderInput", props => {
      if (isScalarAccessor(props.accessor)) {
        const currentValue = typeof props.currentValue === "string" || props.currentValue === null ? props.currentValue : undefined;
        return /*#__PURE__*/React.createElement(AbColorDataValueInput, _extends({}, props, {
          accessor: props.accessor,
          currentValue: currentValue
        }));
      }
      throw new Error(`Unable to render color input for data value "${props.accessor.name}"`);
    });
  }
  renderValue(props) {
    if (isScalarAccessor(props.accessor)) {
      const value = props.currentValue !== undefined ? props.currentValue : props.accessor.getClientValue(props.subject);
      return typeof value !== "string" || value === "" ? /*#__PURE__*/React.createElement(AbUnsetValue, null) : /*#__PURE__*/React.createElement(AbColorViewer, {
        color: value,
        size: AbSize.MEDIUM
      });
    }
    return null;
  }

  // TODO Implement Grid Methods
}
//# sourceMappingURL=DataValueViews.js.map
